import React, { useCallback } from 'react';
import classNames from 'classnames';
import './InlineActions.less';
import { ITreeNode } from '../Tree/Legacy/TreeNode';
import { InlineAction, SampleTreeType } from './SampleTree';
import { UnitTestsTreeType } from './UnitTestTree';
import { LinkButton } from '../buttons/LinkButton';
import { useRequest } from 'hooks/useRequest';
import {
  SampleCategory,
  SampleForTree,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/Samples';
import { UnitTest } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/UnitTest';
import { IconButton } from '../buttons/IconButton';
interface IInlineActionsProps extends Omit<React.HTMLProps<HTMLDivElement>, 'children'> {
  node: ITreeNode<SampleTreeType> | ITreeNode<UnitTestsTreeType>;
  onAddNewClick?: () => void;
  actions?: InlineAction[];
  canRunAll?: boolean;
  onRunStopAllClick?: () => void;
  hasUnitTests?: boolean;
  successNum?: number;
  failureNum?: number;
  errorNum?: number;
  isRunning?: boolean;
}

function getInlineActionsClassName(classes?: string) {
  return classNames('dp-inline-actions', classes);
}

export function InlineActions({
  hasUnitTests,
  successNum,
  failureNum,
  errorNum,
  isRunning,
  node,
  className,
  onAddNewClick,
  actions,
  canRunAll,
  onRunStopAllClick,
  ...otherProps
}: IInlineActionsProps) {
  const entity: SampleCategory | SampleForTree | UnitTest | undefined = node.data?.entity;

  const { makeRequest: handleClick, isLoading: disable } = useRequest((action, element) => {
    if (entity) {
      return action(entity.Id, element);
    }
  });

  const getRunAll = useCallback(() => {
    const hasSuccess: boolean = typeof successNum === 'number' && successNum > 0;
    const hasFailures: boolean = typeof failureNum === 'number' && failureNum > 0;
    const haserror: boolean = typeof errorNum === 'number' && errorNum > 0;
    return (
      <>
        {hasSuccess && (
          <>
            <span className="dp-inline-actions__text success-text">Success:</span>
            <span className="dp-inline-actions__text success-text" id="successNumLabel">
              {successNum}
            </span>
          </>
        )}
        {hasFailures && (
          <>
            <span className="dp-inline-actions__text failure-text">Failure:</span>
            <span className="dp-inline-actions__text failure-text" id="failureNumLabel">
              {failureNum}
            </span>
          </>
        )}
        {haserror && (
          <>
            <span className="dp-inline-actions__text failure-text">Error:</span>
            <span className="dp-inline-actions__text failure-text" id="errorNumLabel">
              {failureNum}
            </span>
          </>
        )}
        {(hasSuccess || hasFailures || haserror) && <span className="dp-inline-actions__separator"></span>}
        {(isRunning || hasUnitTests || haserror) && (
          <>
            <LinkButton
              color="primary"
              className={'dp-inline-actions__button'}
              onClick={() => {
                onRunStopAllClick && onRunStopAllClick();
              }}
            >
              {isRunning ? 'Stop All' : 'Run All'}
            </LinkButton>
            <span className="dp-inline-actions__separator"></span>
          </>
        )}
      </>
    );
  }, [isRunning, successNum, failureNum, errorNum, onRunStopAllClick, hasUnitTests]);

  return (
    <div className={getInlineActionsClassName(className)}>
      {node.nodeId === 'root' ? (
        <>
          {canRunAll && getRunAll()}
          <LinkButton
            color="primary"
            className={'dp-inline-actions__button'}
            onClick={() => {
              onAddNewClick && onAddNewClick();
            }}
          >
            Add New
          </LinkButton>
        </>
      ) : entity ? (
        <>
          {(actions ?? []).map((ia, index) => {
            return !ia.canBeAdded || ia.canBeAdded(entity) ? (
              <IconButton
                disabled={disable}
                key={`${entity.Id}_${index}`}
                iconClassName={classNames(ia.className, 'dp-inline-actions__icon')}
                iconSize="normal"
                title={ia.title}
                onClick={event => handleClick(ia.action, event.target as HTMLElement)}
              />
            ) : null;
          })}
        </>
      ) : null}
    </div>
  );
}
